import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router";

import { groupOfPath, type NavItem, type VisibleGroup } from "@/app/nav";
import { NavBadge } from "@/app/navBadges";
import { cn } from "@/lib/utils";

interface SidebarProps {
  groups: readonly VisibleGroup[];
  /** `rail` = icons only (tablet 768–1279). */
  mode: "full" | "rail";
}

function ItemLink({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const label = t(`nav.items.${item.key}`);
  if (item.external) {
    return (
      <a
        href={item.to}
        className="flex min-h-touch items-center gap-2 rounded-md px-3 text-sm hover:bg-accent"
      >
        <span className="truncate">{label}</span>
        <ExternalLink className="size-3.5 shrink-0" aria-hidden />
        <span className="sr-only">{t("nav.external")}</span>
      </a>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "flex min-h-touch items-center gap-2 rounded-md px-3 text-sm",
          isActive ? "bg-foreground font-semibold text-background" : "hover:bg-accent",
        )
      }
    >
      <span className="truncate">{label}</span>
      <NavBadge target={`item:${item.key}`} />
      {item.comingSoon ? (
        <span className="ml-auto shrink-0 rounded-sm border px-1 text-[0.65rem] leading-tight">
          {t("nav.coming_soon")}
        </span>
      ) : null}
    </NavLink>
  );
}

/**
 * Left menu (design/04 §1): the full tree on ≥ 1280 px (the current group opens its entries);
 * an icon rail on tablets, where the entries of a group are the tabs on top of the page.
 */
export function Sidebar({ groups, mode }: SidebarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const current = groupOfPath(pathname);

  if (mode === "rail") {
    return (
      <nav
        aria-label={t("nav.main")}
        data-slot="sidebar"
        data-mode="rail"
        className="w-[4.5rem] shrink-0 border-r"
      >
        <ul className="sticky top-0 flex flex-col items-center gap-1 py-3">
          {groups.map((group) => {
            const Icon = group.icon;
            const label = t(`nav.groups.${group.key}`);
            return (
              <li key={group.key}>
                <NavLink
                  to={group.to}
                  aria-label={label}
                  title={label}
                  aria-current={current === group.key ? "page" : undefined}
                  className={cn(
                    "flex size-[56px] items-center justify-center rounded-md",
                    current === group.key ? "bg-foreground text-background" : "hover:bg-accent",
                  )}
                >
                  <span className="relative">
                    <Icon className="size-6" aria-hidden />
                    <NavBadge
                      target={`group:${group.key}`}
                      className="absolute -top-2 -right-3 inline-flex min-w-5 items-center justify-center rounded-full bg-[#b91c1c] px-1 text-[0.65rem] leading-5 font-bold text-white"
                    />
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  }

  return (
    <nav
      aria-label={t("nav.main")}
      data-slot="sidebar"
      data-mode="full"
      className="w-64 shrink-0 border-r"
    >
      <ul className="sticky top-0 flex max-h-dvh flex-col gap-0.5 overflow-y-auto p-3">
        {groups.map((group) => {
          const Icon = group.icon;
          const open = current === group.key;
          const single = group.items.length === 1 && group.items[0]?.to === group.to;
          return (
            <li key={group.key} data-group={group.key}>
              {single ? (
                <NavLink
                  to={group.to}
                  end={group.items[0]?.end}
                  className={({ isActive }) =>
                    cn(
                      "flex min-h-touch items-center gap-3 rounded-md px-3 font-medium",
                      isActive ? "bg-foreground text-background" : "hover:bg-accent",
                    )
                  }
                >
                  <Icon className="size-5 shrink-0" aria-hidden />
                  {t(`nav.groups.${group.key}`)}
                </NavLink>
              ) : (
                <>
                  <NavLink
                    to={group.to}
                    aria-expanded={open}
                    className={cn(
                      "flex min-h-touch items-center gap-3 rounded-md px-3 font-medium hover:bg-accent",
                      open ? "font-bold" : "",
                    )}
                  >
                    <Icon className="size-5 shrink-0" aria-hidden />
                    {t(`nav.groups.${group.key}`)}
                    <NavBadge target={`group:${group.key}`} />
                  </NavLink>
                  {open ? (
                    <ul className="mt-0.5 mb-1 ml-5 flex flex-col gap-0.5 border-l pl-3">
                      {group.items.map((item) => (
                        <li key={item.key}>
                          <ItemLink item={item} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Tablet/phone: the entries of the current group as a tab strip above the page. */
export function SectionTabs({ groups }: { groups: readonly VisibleGroup[] }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const current = groupOfPath(pathname);
  const group = groups.find((g) => g.key === current);
  if (!group || group.items.length < 2) {
    return null;
  }
  return (
    <nav
      aria-label={t("nav.section")}
      data-slot="section-tabs"
      className="-mx-4 mb-4 overflow-x-auto px-4 sm:-mx-6 sm:px-6"
    >
      <ul className="flex w-max gap-1 border-b">
        {group.items.map((item) => (
          <li key={item.key}>
            {item.external ? (
              <a
                href={item.to}
                className="inline-flex min-h-touch items-center px-3 text-sm whitespace-nowrap"
              >
                {t(`nav.items.${item.key}`)}
              </a>
            ) : (
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "inline-flex min-h-touch items-center border-b-2 px-3 text-sm whitespace-nowrap",
                    isActive ? "border-foreground font-semibold" : "border-transparent",
                  )
                }
              >
                {t(`nav.items.${item.key}`)}
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
